import { useState, useEffect, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { useAuthStore } from '@/stores/authStore'
import { userService } from '@/services/user.service'
import { authService } from '@/services/auth.service'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useToast } from '@/components/ui/use-toast'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { User, Mail, Phone, Shield, Calendar, Building, Camera, Loader2 } from 'lucide-react'
import { format } from 'date-fns'
import type { UserUpdateDto, UserPreferencesUpdate } from '@/types/user.types'

const profileSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  phoneNumber: z.string().optional(),
  contactPref: z.enum(['email', 'sms', 'both']),
})

const passwordSchema = z.object({
  newPassword: z.string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[0-9]/, 'Password must contain at least one number'),
  confirmPassword: z.string(),
}).refine(data => data.newPassword === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
})

const preferencesSchema = z.object({
  unitsTemperature: z.enum(['celsius', 'fahrenheit']),
  unitsLength: z.enum(['metric', 'imperial']),
  unitsWeight: z.enum(['metric', 'imperial']),
  unitsSpeed: z.enum(['kph', 'mph']),
  timezone: z.string(),
  dashboardColumns: z.enum(['1', '2', '3', '4']),
})

type ProfileFormValues = z.infer<typeof profileSchema>
type PasswordFormValues = z.infer<typeof passwordSchema>
type PreferencesFormValues = z.infer<typeof preferencesSchema>

export default function UserProfilePage() {
  const { user, setUser, linkedAccounts, setLinkedAccounts } = useAuthStore()
  const { toast } = useToast()
  const [loading, setLoading] = useState(false)
  const [changingPassword, setChangingPassword] = useState(false)
  const [savingPreferences, setSavingPreferences] = useState(false)
  const [fetchingProfile, setFetchingProfile] = useState(true)
  const [uploadingPhoto, setUploadingPhoto] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const profileForm = useForm<ProfileFormValues>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      phoneNumber: user?.phoneNumber || '',
      contactPref: user?.contactPref || 'email',
    },
  })

  const passwordForm = useForm<PasswordFormValues>({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      newPassword: '',
      confirmPassword: '',
    },
  })

  const preferencesForm = useForm<PreferencesFormValues>({
    resolver: zodResolver(preferencesSchema),
    defaultValues: {
      unitsTemperature: user?.preferences?.unitsTemperature || 'celsius',
      unitsLength: user?.preferences?.unitsLength || 'metric',
      unitsWeight: user?.preferences?.unitsWeight || 'metric',
      unitsSpeed: user?.preferences?.unitsSpeed || 'kph',
      timezone: user?.preferences?.timezone || 'UTC',
      dashboardColumns: user?.preferences?.dashboardColumns || '2',
    },
  })

  // Fetch fresh profile data when component mounts
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setFetchingProfile(true)
        const response = await authService.getMe()
        setUser(response.user)
        setLinkedAccounts(response.linkedAccounts)
      } catch (error) {
        console.error('Failed to fetch profile:', error)
        toast({
          title: 'Error',
          description: 'Failed to load profile data',
          variant: 'destructive',
        })
      } finally {
        setFetchingProfile(false)
      }
    }

    fetchProfile()
  }, [setUser, setLinkedAccounts, toast])

  // Update forms when user data changes
  useEffect(() => {
    if (user && user.preferences) {
      profileForm.reset({
        firstName: user.firstName,
        lastName: user.lastName,
        phoneNumber: user.phoneNumber || '',
        contactPref: user.contactPref,
      })
      preferencesForm.reset({
        unitsTemperature: user.preferences.unitsTemperature || 'celsius',
        unitsLength: user.preferences.unitsLength || 'metric',
        unitsWeight: user.preferences.unitsWeight || 'metric',
        unitsSpeed: user.preferences.unitsSpeed || 'kph',
        timezone: user.preferences.timezone || 'UTC',
        dashboardColumns: user.preferences.dashboardColumns || '2',
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  const onProfileSubmit = async (data: ProfileFormValues) => {
    if (!user) return

    try {
      setLoading(true)
      const updateData: UserUpdateDto = {
        firstName: data.firstName,
        lastName: data.lastName,
        phoneNumber: data.phoneNumber || undefined,
        contactPref: data.contactPref,
      }

      const updatedUser = await userService.updateUser(user.id, updateData)
      setUser(updatedUser)

      toast({
        title: 'Success',
        description: 'Your profile has been updated',
      })
    } catch (error: any) {
      console.error('Failed to update profile:', error)
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to update profile',
        variant: 'destructive',
      })
    } finally {
      setLoading(false)
    }
  }

  const onPasswordSubmit = async (data: PasswordFormValues) => {
    try {
      setChangingPassword(true)
      await authService.changePassword(data.newPassword)
      toast({
        title: 'Success',
        description: 'Your password has been changed successfully',
      })
      passwordForm.reset()
    } catch (error: any) {
      console.error('Failed to change password:', error)
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to change password',
        variant: 'destructive',
      })
    } finally {
      setChangingPassword(false)
    }
  }

  const onPreferencesSubmit = async (data: PreferencesFormValues) => {
    if (!user) return

    try {
      setSavingPreferences(true)
      const preferencesData: UserPreferencesUpdate = {
        units_temperature: data.unitsTemperature,
        units_length: data.unitsLength,
        units_weight: data.unitsWeight,
        units_speed: data.unitsSpeed,
        timezone: data.timezone,
        dashboard_columns: data.dashboardColumns,
      }

      const updatedUser = await userService.updateUserSettings(user.id, preferencesData)
      setUser(updatedUser)

      toast({
        title: 'Success',
        description: 'Your preferences have been updated',
      })
    } catch (error: any) {
      console.error('Failed to update preferences:', error)
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to update preferences',
        variant: 'destructive',
      })
    } finally {
      setSavingPreferences(false)
    }
  }

  const handlePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file || !user) return

    // Reset input so the same file can be re-selected
    event.target.value = ''

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif']
    if (!allowedTypes.includes(file.type)) {
      toast({
        title: 'Invalid file type',
        description: 'Please upload a JPG, PNG, or GIF image',
        variant: 'destructive',
      })
      return
    }

    // Validate file size (5MB max)
    const maxSize = 5 * 1024 * 1024
    if (file.size > maxSize) {
      toast({
        title: 'File too large',
        description: 'Profile photo must be less than 5MB',
        variant: 'destructive',
      })
      return
    }

    try {
      setUploadingPhoto(true)
      const { avatarUrl } = await userService.uploadProfilePhoto(user.id, file)
      setUser({ ...user, avatarUrl })
      toast({
        title: 'Success',
        description: 'Profile photo updated',
      })
    } catch (error: any) {
      console.error('Failed to upload profile photo:', error)
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to upload profile photo',
        variant: 'destructive',
      })
    } finally {
      setUploadingPhoto(false)
    }
  }

  const getInitials = () => {
    if (!user) return 'U'
    const first = user.firstName?.[0] || ''
    const last = user.lastName?.[0] || ''
    if (!first && !last) return user.email?.[0]?.toUpperCase() || 'U'
    return `${first}${last}`.toUpperCase()
  }

  if (fetchingProfile || !user) {
    return (
      <div className="container mx-auto py-6">
        <Card>
          <CardContent className="py-8">
            <p className="text-center text-muted-foreground">
              {fetchingProfile ? 'Loading profile...' : 'No user data available'}
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="container mx-auto py-6 max-w-4xl">
      <div className="space-y-6">
        {/* Profile Header */}
        <Card>
          <CardHeader>
            <div className="flex items-center space-x-4">
              <button
                type="button"
                className="relative group cursor-pointer rounded-full focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingPhoto}
                aria-label="Upload profile photo"
              >
                <Avatar className="h-20 w-20">
                  <AvatarImage src={user.avatarUrl} />
                  <AvatarFallback className="text-lg">{getInitials()}</AvatarFallback>
                </Avatar>
                <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity">
                  {uploadingPhoto ? (
                    <Loader2 className="h-6 w-6 text-white animate-spin" />
                  ) : (
                    <Camera className="h-6 w-6 text-white" />
                  )}
                </div>
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif"
                className="hidden"
                onChange={handlePhotoUpload}
              />
              <div className="space-y-1">
                <CardTitle className="text-2xl">{user.displayName}</CardTitle>
                <CardDescription className="flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  {user.email}
                </CardDescription>
                <div className="flex gap-2">
                  {user.isSuperUser && (
                    <Badge variant="secondary" className="flex items-center gap-1">
                      <Shield className="h-3 w-3" />
                      Super Admin
                    </Badge>
                  )}
                  {user.isBillingContact && (
                    <Badge variant="outline">Billing Contact</Badge>
                  )}
                  {user.isActive ? (
                    <Badge variant="default">Active</Badge>
                  ) : (
                    <Badge variant="destructive">Inactive</Badge>
                  )}
                </div>
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Profile Tabs */}
        <Tabs defaultValue="personal" className="space-y-4">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="personal">Personal Info</TabsTrigger>
            <TabsTrigger value="preferences">Preferences</TabsTrigger>
            <TabsTrigger value="accounts">Linked Accounts</TabsTrigger>
            <TabsTrigger value="security">Security</TabsTrigger>
          </TabsList>

          <TabsContent value="personal" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Personal Information</CardTitle>
                <CardDescription>
                  Update your personal details and contact preferences
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Form {...profileForm}>
                  <form onSubmit={profileForm.handleSubmit(onProfileSubmit)} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <FormField
                        control={profileForm.control}
                        name="firstName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>First Name</FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={profileForm.control}
                        name="lastName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Last Name</FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={profileForm.control}
                      name="phoneNumber"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Phone Number</FormLabel>
                          <FormControl>
                            <Input type="tel" placeholder="+1 (555) 000-0000" {...field} />
                          </FormControl>
                          <FormDescription>
                            Used for SMS notifications if enabled
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={profileForm.control}
                      name="contactPref"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Contact Preference</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="email">Email Only</SelectItem>
                              <SelectItem value="sms">SMS Only</SelectItem>
                              <SelectItem value="both">Email and SMS</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormDescription>
                            How would you like to receive notifications?
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="pt-4">
                      <Button type="submit" disabled={loading}>
                        {loading ? 'Saving...' : 'Save Changes'}
                      </Button>
                    </div>
                  </form>
                </Form>
              </CardContent>
            </Card>

            {/* Account Information */}
            <Card>
              <CardHeader>
                <CardTitle>Account Information</CardTitle>
                <CardDescription>
                  Your account details and system information
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm text-muted-foreground">User ID</Label>
                      <p className="text-sm font-mono">{user.id}</p>
                    </div>
                    <div>
                      <Label className="text-sm text-muted-foreground">Email</Label>
                      <p className="text-sm">{user.email}</p>
                    </div>
                    <div>
                      <Label className="text-sm text-muted-foreground">Default Account</Label>
                      <p className="text-sm">{user.defaultAccountName || 'Not set'}</p>
                    </div>
                    <div>
                      <Label className="text-sm text-muted-foreground">Account Created</Label>
                      <p className="text-sm">
                        {user.createdAt ? format(new Date(user.createdAt), 'PPP') : 'Unknown'}
                      </p>
                    </div>
                    <div>
                      <Label className="text-sm text-muted-foreground">Last Updated</Label>
                      <p className="text-sm">
                        {user.updatedAt ? format(new Date(user.updatedAt), 'PPP') : 'Unknown'}
                      </p>
                    </div>
                    <div>
                      <Label className="text-sm text-muted-foreground">Last Login</Label>
                      <p className="text-sm">
                        {user.lastLogin ? format(new Date(user.lastLogin), 'PPP') : 'Never'}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="preferences" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Display Preferences</CardTitle>
                <CardDescription>
                  Customize how data is displayed in the application
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Form {...preferencesForm}>
                  <form onSubmit={preferencesForm.handleSubmit(onPreferencesSubmit)} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <FormField
                        control={preferencesForm.control}
                        name="unitsTemperature"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Temperature Units</FormLabel>
                            <Select onValueChange={field.onChange} value={field.value}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="celsius">Celsius (°C)</SelectItem>
                                <SelectItem value="fahrenheit">Fahrenheit (°F)</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={preferencesForm.control}
                        name="unitsLength"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Distance Units</FormLabel>
                            <Select onValueChange={field.onChange} value={field.value}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="metric">Metric (m, km)</SelectItem>
                                <SelectItem value="imperial">Imperial (ft, mi)</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={preferencesForm.control}
                        name="unitsWeight"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Weight Units</FormLabel>
                            <Select onValueChange={field.onChange} value={field.value}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="metric">Metric (kg)</SelectItem>
                                <SelectItem value="imperial">Imperial (lb)</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={preferencesForm.control}
                        name="unitsSpeed"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Speed Units</FormLabel>
                            <Select onValueChange={field.onChange} value={field.value}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="kph">km/h</SelectItem>
                                <SelectItem value="mph">mph</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={preferencesForm.control}
                        name="timezone"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Timezone</FormLabel>
                            <Select onValueChange={field.onChange} value={field.value}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="UTC">UTC</SelectItem>
                                <SelectItem value="America/New_York">Eastern Time</SelectItem>
                                <SelectItem value="America/Chicago">Central Time</SelectItem>
                                <SelectItem value="America/Denver">Mountain Time</SelectItem>
                                <SelectItem value="America/Los_Angeles">Pacific Time</SelectItem>
                                <SelectItem value="Australia/Sydney">Sydney</SelectItem>
                                <SelectItem value="Europe/London">London</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={preferencesForm.control}
                        name="dashboardColumns"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Dashboard Layout</FormLabel>
                            <Select onValueChange={field.onChange} value={field.value}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="1">1 Column</SelectItem>
                                <SelectItem value="2">2 Columns</SelectItem>
                                <SelectItem value="3">3 Columns</SelectItem>
                                <SelectItem value="4">4 Columns</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className="pt-4">
                      <Button type="submit" disabled={savingPreferences}>
                        {savingPreferences ? 'Saving...' : 'Save Preferences'}
                      </Button>
                    </div>
                  </form>
                </Form>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="accounts" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Linked Accounts</CardTitle>
                <CardDescription>
                  Accounts you have access to in the system
                </CardDescription>
              </CardHeader>
              <CardContent>
                {linkedAccounts && linkedAccounts.length > 0 ? (
                  <div className="space-y-3">
                    {linkedAccounts.map((account) => (
                      <div
                        key={account.id}
                        className="flex items-center justify-between border rounded-lg p-3"
                      >
                        <div className="flex items-center gap-3">
                          <Building className="h-5 w-5 text-muted-foreground" />
                          <div>
                            <p className="font-medium">{account.name}</p>
                            <p className="text-sm text-muted-foreground">ID: {account.id}</p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          {account.isDefault && (
                            <Badge variant="default">Default</Badge>
                          )}
                          {account.isAdmin && (
                            <Badge variant="secondary">Admin</Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No linked accounts available
                  </p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="security" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Change Password</CardTitle>
                <CardDescription>
                  Update your password to keep your account secure
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Form {...passwordForm}>
                  <form onSubmit={passwordForm.handleSubmit(onPasswordSubmit)} className="space-y-4">
                    <FormField
                      control={passwordForm.control}
                      name="newPassword"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>New Password</FormLabel>
                          <FormControl>
                            <Input type="password" {...field} />
                          </FormControl>
                          <FormDescription>
                            Must be at least 8 characters with uppercase, lowercase, and numbers
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={passwordForm.control}
                      name="confirmPassword"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Confirm New Password</FormLabel>
                          <FormControl>
                            <Input type="password" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="pt-4">
                      <Button type="submit" disabled={changingPassword}>
                        {changingPassword ? 'Changing Password...' : 'Change Password'}
                      </Button>
                    </div>
                  </form>
                </Form>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  )
}